import { useState, useEffect, useCallback } from "react";
import { GRID, RIGHT_PADDING_PERCENT } from "../model/constants";
import type { ZoomRange } from "../model/types";
import type { ServerBasePrice } from "services/types";
import type { CandleWithFlag } from "../model/types";

interface UseChartAutoScrollParams {
  dataRef: React.MutableRefObject<(ServerBasePrice | CandleWithFlag)[]>;
  containerRef: React.RefObject<HTMLDivElement>;
  zoomRange: ZoomRange;
  zoomRangeRef: React.MutableRefObject<ZoomRange>;
  autoScrollEnabledRef: React.MutableRefObject<boolean>;
  smoothScrollTo: (newStart: number, newEnd: number) => void;
}

export function useChartAutoScroll({
  dataRef,
  containerRef,
  zoomRange,
  zoomRangeRef,
  autoScrollEnabledRef,
  smoothScrollTo,
}: UseChartAutoScrollParams) {
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    if (dataRef.current.length === 0 || !containerRef.current) return;

    const lastCandleIndex = dataRef.current.length - 1;
    const { start, end } = zoomRangeRef.current;

    const containerWidth = containerRef.current.clientWidth;
    const chartWidth = containerWidth - GRID.left - GRID.right;
    const visibleCandles = end - start + 1;
    const candleWidth = chartWidth / visibleCandles;
    const priceScaleBuffer = Math.ceil((GRID.right + 65) / candleWidth);
    const autoScrollThreshold = end - priceScaleBuffer;

    setShowScrollButton(lastCandleIndex >= autoScrollThreshold);
  }, [dataRef.current.length, zoomRange]);

  const scrollToLastCandle = useCallback(() => {
    if (!containerRef.current || dataRef.current.length === 0) return;

    const containerWidth = containerRef.current.clientWidth;
    const chartWidth = containerWidth - GRID.left - GRID.right;
    const visibleCandles = zoomRangeRef.current.end - zoomRangeRef.current.start + 1;
    const candleWidth = chartWidth / visibleCandles;
    const rightPadding = Math.ceil((chartWidth * RIGHT_PADDING_PERCENT) / candleWidth);

    const lastCandleIndex = dataRef.current.length - 1;
    const end = lastCandleIndex + rightPadding;
    const start = Math.max(0, end - (zoomRangeRef.current.end - zoomRangeRef.current.start));

    smoothScrollTo(start, end);
    autoScrollEnabledRef.current = true;
  }, [dataRef.current.length, smoothScrollTo]);

  return { showScrollButton, scrollToLastCandle };
}
